import React, { useState } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Star,
  Clock,
  Users,
  AlertTriangle,
  Target,
  Settings,
  Plus,
  X,
  AlertCircle,
  Info,
  CheckCircle,
  MessageCircle,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import priorityMasterService from '../../services/priorityMasterService'

const PriorityMasterForm = () => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basicInfo')

  const [formData, setFormData] = useState({
    priorityCode: '',
    priorityName: '',
    priorityDescription: '',
    priorityLevel: 5,
    priorityCategory: 'Medium',
    priorityColor: '#3B82F6',
    priorityIcon: 'info',
    isDefault: false,
    sortOrder: 0,
    responseTimeHours: 24,
    resolutionTimeHours: 72,
    escalationTimeHours: 48,
    notifyOnCreation: true,
    notifyOnEscalation: false,
    notifyStakeholders: false,
    businessImpact: 'Medium',
    urgencyLevel: 'Medium',
    averageResolutionTime: 0,
    escalationRate: 0
  })

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const validateForm = () => {
    if (!formData.priorityCode.trim()) {
      toast.error('Priority Code is required')
      return false
    }
    if (!formData.priorityName.trim()) {
      toast.error('Priority Name is required')
      return false
    }
    if (!formData.priorityDescription.trim()) {
      toast.error('Priority Description is required')
      return false
    }
    if (formData.priorityLevel < 1 || formData.priorityLevel > 10) {
      toast.error('Priority Level must be between 1 and 10')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      toast.info('Creating Priority Master record...')
      const response = await priorityMasterService.createPriorityMaster({
        priorityCode: formData.priorityCode.toUpperCase(),
        priorityName: formData.priorityName,
        priorityDescription: formData.priorityDescription,
        priorityLevel: formData.priorityLevel,
        priorityCategory: formData.priorityCategory,
        priorityColor: formData.priorityColor,
        priorityIcon: formData.priorityIcon,
        isDefault: formData.isDefault,
        sortOrder: formData.sortOrder,
        responseTimeHours: formData.responseTimeHours,
        resolutionTimeHours: formData.resolutionTimeHours,
        escalationTimeHours: formData.escalationTimeHours,
        notifyOnCreation: formData.notifyOnCreation,
        notifyOnEscalation: formData.notifyOnEscalation,
        notifyStakeholders: formData.notifyStakeholders,
        businessImpact: formData.businessImpact,
        urgencyLevel: formData.urgencyLevel,
        averageResolutionTime: formData.averageResolutionTime,
        escalationRate: formData.escalationRate
      })

      if (response.success) {
        toast.success('Priority Master record created successfully!', { autoClose: 2000 })
        navigate('/projects/priority-master')
      } else {
        toast.error(response.message || 'Failed to create Priority Master record')
      }
    } catch (error) {
      console.error('Error creating Priority Master record:', error)
      if (error.response?.status === 400 && error.response.data?.message.includes('already exists')) {
        toast.error('Priority Code already exists. Please use a different code.')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action.')
      } else {
        toast.error('Failed to create Priority Master record. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      priorityCode: '',
      priorityName: '',
      priorityDescription: '',
      priorityLevel: 5,
      priorityCategory: 'Medium',
      priorityColor: '#3B82F6',
      priorityIcon: 'info',
      isDefault: false,
      sortOrder: 0,
      responseTimeHours: 24,
      resolutionTimeHours: 72,
      escalationTimeHours: 48,
      notifyOnCreation: true,
      notifyOnEscalation: false,
      notifyStakeholders: false,
      businessImpact: 'Medium',
      urgencyLevel: 'Medium',
      averageResolutionTime: 0,
      escalationRate: 0
    })
    toast.info('Form reset to default values')
  }

  const handleBack = () => {
    navigate('/projects/priority-master')
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Masters', path: '/projects?tab=masters', onClick: () => navigate('/projects?tab=masters') },
    { label: 'Priority Master', path: '/projects/priority-master', onClick: () => navigate('/projects/priority-master') },
    { label: 'Create New', path: '/projects/priority-master/new', isCurrent: true }
  ]

  const priorityCategories = [
    'Critical',
    'High',
    'Medium',
    'Low',
    'Informational'
  ]

  const priorityIcons = [
    'alert-triangle',
    'alert-circle',
    'info',
    'check-circle',
    'message-circle'
  ]

  const impactLevels = [
    'Critical',
    'High',
    'Medium',
    'Low'
  ]

  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'alert-triangle': return <AlertTriangle size={18} />
      case 'alert-circle': return <AlertCircle size={18} />
      case 'info': return <Info size={18} />
      case 'check-circle': return <CheckCircle size={18} />
      case 'message-circle': return <MessageCircle size={18} />
      default: return <Star size={18} />
    }
  }

  const helpContent = (
    <>
      <h3>What is Priority Master?</h3>
      <p>Priority Master lets you <strong>define project priorities</strong> such as Low, Medium, High, and Critical. You can set priority level (1–10), category, color, icon, response/resolution/escalation times, and notification and business-impact options.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Info</strong> – Priority code, name, description, level, category, color, icon, sort order, default flag.</p>
      <p><strong>SLAs &amp; Notifications</strong> – Response, resolution, and escalation time (hours); notify on creation/escalation/stakeholders; business impact and urgency.</p>
      <h3>How to use</h3>
      <p>Fill required fields (code, name, description). Priority level must be 1–10. Use <strong>Create Priority</strong> to save. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Priority Master Entry</h1>
          <p>Create and manage priority level information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Priority Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'basicInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('basicInfo')}
          >
            <Star size={18} />
            <span>Basic Information</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'slaSettings' ? 'active' : ''}`}
            onClick={() => setActiveTab('slaSettings')}
          >
            <Clock size={18} />
            <span>SLA Settings</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <Users size={18} />
            <span>Notifications</span>
          </button>
        </div>
          
        <form className="form-content">

          {/* Basic Information Tab */}
          {activeTab === 'basicInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Basic Information</h3>
              </div>
              
              {/* Row 1: Priority Code, Name, Level, Category */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="priorityCode" className="form-label required">
                    Priority Code
                  </label>
                  <input
                    id="priorityCode"
                    type="text"
                    value={formData.priorityCode}
                    onChange={(e) => handleInputChange('priorityCode', e.target.value)}
                    className="form-input"
                    placeholder="e.g., CRITICAL, HIGH, MEDIUM"
                    maxLength={20}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="priorityName" className="form-label required">
                    Priority Name
                  </label>
                  <input
                    id="priorityName"
                    type="text"
                    value={formData.priorityName}
                    onChange={(e) => handleInputChange('priorityName', e.target.value)}
                    className="form-input"
                    placeholder="e.g., Critical, High, Medium"
                    maxLength={100}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="priorityLevel" className="form-label required">
                    Priority Level
                  </label>
                  <input
                    id="priorityLevel"
                    type="number"
                    value={formData.priorityLevel}
                    onChange={(e) => handleInputChange('priorityLevel', parseInt(e.target.value) || 1)}
                    className="form-input"
                    min="1"
                    max="10"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="priorityCategory" className="form-label required">
                    Priority Category
                  </label>
                  <select
                    id="priorityCategory"
                    value={formData.priorityCategory}
                    onChange={(e) => handleInputChange('priorityCategory', e.target.value)}
                    className="form-input"
                  >
                    {priorityCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Description */}
              <div className="form-group">
                <label htmlFor="priorityDescription" className="form-label required">
                  Priority Description
                </label>
                <textarea
                  id="priorityDescription"
                  value={formData.priorityDescription}
                  onChange={(e) => handleInputChange('priorityDescription', e.target.value)}
                  className="form-textarea"
                  placeholder="Describe the priority level, its impact, and when it should be used..."
                  rows={4}
                />
              </div>

              {/* Row 3: Visual Settings */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="priorityColor" className="form-label">
                    Priority Color
                  </label>
                  <input
                    id="priorityColor"
                    type="color"
                    value={formData.priorityColor}
                    onChange={(e) => handleInputChange('priorityColor', e.target.value)}
                    className="form-input"
                    style={{ height: '40px' }}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="priorityIcon" className="form-label">
                    Priority Icon
                  </label>
                  <select
                    id="priorityIcon"
                    value={formData.priorityIcon}
                    onChange={(e) => handleInputChange('priorityIcon', e.target.value)}
                    className="form-input"
                  >
                    {priorityIcons.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="sortOrder" className="form-label">
                    Sort Order
                  </label>
                  <input
                    id="sortOrder"
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => handleInputChange('sortOrder', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                  />
                </div>
              </div>

              {/* Row 4: Business Impact and Urgency */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="businessImpact" className="form-label">
                    Business Impact
                  </label>
                  <select
                    id="businessImpact"
                    value={formData.businessImpact}
                    onChange={(e) => handleInputChange('businessImpact', e.target.value)}
                    className="form-input"
                  >
                    {impactLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="urgencyLevel" className="form-label">
                    Urgency Level
                  </label>
                  <select
                    id="urgencyLevel"
                    value={formData.urgencyLevel}
                    onChange={(e) => handleInputChange('urgencyLevel', e.target.value)}
                    className="form-input"
                  >
                    {impactLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 5: Default Checkbox */}
              <div className="form-group">
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                  />
                  <span>Set as Default Priority</span>
                </label>
              </div>
            </div>
          )}

          {/* SLA Settings Tab */}
          {activeTab === 'slaSettings' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>SLA Settings</h3>
              </div>
              
              {/* Response Time */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="responseTimeHours" className="form-label">
                    Response Time (hours)
                  </label>
                  <input
                    id="responseTimeHours"
                    type="number"
                    value={formData.responseTimeHours}
                    onChange={(e) => handleInputChange('responseTimeHours', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="resolutionTimeHours" className="form-label">
                    Resolution Time (hours)
                  </label>
                  <input
                    id="resolutionTimeHours"
                    type="number"
                    value={formData.resolutionTimeHours}
                    onChange={(e) => handleInputChange('resolutionTimeHours', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="escalationTimeHours" className="form-label">
                    Escalation Time (hours)
                  </label>
                  <input
                    id="escalationTimeHours"
                    type="number"
                    value={formData.escalationTimeHours}
                    onChange={(e) => handleInputChange('escalationTimeHours', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                  />
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="averageResolutionTime" className="form-label">
                    Average Resolution Time (hours)
                  </label>
                  <input
                    id="averageResolutionTime"
                    type="number"
                    value={formData.averageResolutionTime}
                    onChange={(e) => handleInputChange('averageResolutionTime', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="escalationRate" className="form-label">
                    Escalation Rate (%)
                  </label>
                  <input
                    id="escalationRate"
                    type="number"
                    value={formData.escalationRate}
                    onChange={(e) => handleInputChange('escalationRate', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    max="100"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Notification Settings</h3>
              </div>
              
              <div className="grid-row-3">
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.notifyOnCreation}
                      onChange={(e) => handleInputChange('notifyOnCreation', e.target.checked)}
                    />
                    <span>Notify on Creation</span>
                  </label>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.notifyOnEscalation}
                      onChange={(e) => handleInputChange('notifyOnEscalation', e.target.checked)}
                    />
                    <span>Notify on Escalation</span>
                  </label>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.notifyStakeholders}
                      onChange={(e) => handleInputChange('notifyStakeholders', e.target.checked)}
                    />
                    <span>Notify Stakeholders</span>
                  </label>
                </div>
              </div>
            </div>
          )}

        </form>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="btn-secondary"
            disabled={loading}
          >
            <RotateCcw size={16} />
            Reset
          </button>
          
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary"
            disabled={loading}
          >
            <Save size={16} />
            {loading ? 'Creating...' : 'Create Priority'}
          </button>
        </div>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Priority Master – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default PriorityMasterForm

